import { TableOfContents } from "@/components/table-of-contents";
import { seo } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";
export const Route = createFileRoute("/legal/_layout/terms")({
  component: TermsOfService,
  head: () => {
    const title = "Terms of Service | Inkriv";
    const description =
      "Read the terms and conditions for using Inkriv's AI writing platform.";
    const image = "https://inkriv.com/legal.jpg";

    return {
      title,
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        ...seo({ title, description, image }),
      ],
    };
  },
});

function TermsOfService() {
  const sections = [
    { id: "introduction", title: "Introduction" },
    { id: "using-our-service", title: "Using Our Service" },
    { id: "account-responsibilities", title: "Account Responsibilities" },
    { id: "intellectual-property", title: "Intellectual Property" },
    { id: "content-guidelines", title: "Content Guidelines" },
    { id: "payment-terms", title: "Payment Terms" },
    { id: "limitation-of-liability", title: "Limitation of Liability" },
    { id: "termination", title: "Termination" },
    { id: "changes", title: "Changes to Terms" },
    { id: "governing", title: "Governing Law" },
    { id: "contact", title: "Contact Us" },
  ];

  return (
    <div className="flex flex-col md:flex-row gap-8">
      <div className="md:w-1/4 order-2 md:order-1">
        <div className="sticky top-8">
          <TableOfContents sections={sections} />
        </div>
      </div>

      <div className="md:w-3/4 order-1 md:order-2">
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h1 className="text-lg font-bold text-neutral-900mb-6 font-epilogue">
            Terms of Service
          </h1>
          <p className=" text-neutral-700 mb-8 font-satoshi">
            Last Updated: May 2, 2025
          </p>

          <section id="introduction" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900mb-4 font-epilogue">
              Introduction
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              Welcome to Inkriv! These Terms of Service ("Terms") govern your
              use of our website, applications, and related services
              (collectively, the "Service"). By accessing or using Inkriv, you
              agree to these Terms.
            </p>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              We've written these Terms to be as clear and helpful as possible.
              If you have questions, email us at support@inkriv.com.
            </p>
            <div className="bg-warning-50 mt-5 p-4 rounded-md border-l-4 border-warning-400">
              <p className=" text-neutral-800font-satoshi">
                <span className="font-semibold">Please read carefully:</span>{" "}
                These Terms form a legal agreement between you and us. If you
                don't agree with them, please don't use the Service.
              </p>
            </div>
          </section>

          <section id="using-our-service" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900mb-4 font-epilogue">
              Using Our Service
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              Inkriv is designed to help you write in your own voice using AI.
              Here's what to know:
            </p>
            <ul className="list-disc pl-5  text-neutral-800 mb-4 font-satoshi text-xs">
              <li>You need an account to access most features.</li>
              <li>
                The Service is for your personal or business use only and can't
                be resold.
              </li>
              <li>We may change or improve features at any time.</li>
              <li>
                Features and access may vary depending on your subscription
                plan.
              </li>
            </ul>
          </section>

          <section id="account-responsibilities" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900mb-4 font-epilogue">
              Account Responsibilities
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              When you create an account, you agree to:
            </p>
            <div className="bg-neutral-100 p-4 rounded-md mb-4">
              <ul className="list-disc pl-5  text-neutral-800font-satoshi text-xs">
                <li>Provide accurate, up-to-date information</li>
                <li>Keep your login credentials secure</li>
                <li>Be responsible for any activity on your account</li>
                <li>Notify us immediately of unauthorized access</li>
              </ul>
            </div>
            <p className=" text-neutral-800font-satoshi">
              We may suspend or disable your account if we detect violations of
              these Terms or account compromise.
            </p>
          </section>

          <section id="intellectual-property" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900mb-4 font-epilogue">
              Intellectual Property
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className=" font-semibold text-primary-700 mb-2 font-epilogue">
                  Our IP
                </h3>
                <p className=" text-neutral-800 mb-4 font-satoshi">
                  All software, content, branding, and design within Inkriv
                  belong to us or our licensors. You may not copy, distribute,
                  or modify anything from the Service without permission.
                </p>
              </div>

              <div>
                <h3 className=" font-semibold text-primary-700 mb-2 font-epilogue">
                  Your Content
                </h3>
                <p className=" text-neutral-800 mb-4 font-satoshi">
                  You retain ownership of the content you create or upload. You
                  grant us a non-exclusive, worldwide license to store, process,
                  and display that content only to deliver and improve the
                  Service.
                </p>
              </div>

              <div>
                <h3 className=" font-semibold text-primary-700 mb-2 font-epilogue">
                  AI-Generated Content
                </h3>
                <p className=" text-neutral-800 font-satoshi">
                  Content generated using our AI belongs to you. We may use
                  anonymized patterns and usage data to improve our models and
                  platform performance.
                </p>
              </div>
            </div>
          </section>

          <section id="content-guidelines" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900mb-4 font-epilogue">
              Content Guidelines
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              You agree not to upload, generate, or share content that:
            </p>
            <ul className="list-disc pl-5 my-3 text-neutral-800 mb-4 font-satoshi text-xs">
              <li>Violates any laws or intellectual property rights</li>
              <li>Is false, deceptive, or intentionally misleading</li>
              <li>Contains malware or malicious code</li>
              <li>Promotes hate, discrimination, or harassment</li>
              <li>Is pornographic or excessively profane</li>
            </ul>
            <p className=" text-neutral-800 font-satoshi">
              We reserve the right to remove violating content and may suspend
              or terminate your account if necessary.
            </p>
          </section>

          <section id="payment-terms" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900 mb-4 font-epilogue">
              Subscriptions & Payments
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              Some features require a paid subscription. By subscribing, you
              authorize us (or our payment provider) to charge your payment
              method for the applicable subscription fee and any taxes.
            </p>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              Subscriptions automatically renew monthly or annually, depending
              on the plan you choose. You are responsible for canceling before
              the next billing cycle to avoid further charges.
            </p>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              All payments are non-refundable, except in the following cases:
            </p>
            <ul className="list-disc pl-5  text-neutral-800 mb-4 font-satoshi text-xs">
              <li>You cancel within 7 days of your first purchase</li>
              <li>We cancel your subscription without cause</li>
            </ul>
            <div className="bg-secondary-50 mt-4 p-4 rounded-md border-l-4 border-secondary-400">
              <p className=" text-neutral-800 font-satoshi">
                You can cancel anytime by emailing support@inkriv.com. After
                cancellation, you'll continue to have access until the end of
                your current billing period.
              </p>
            </div>
          </section>

          <section id="limitation-of-liability" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900 mb-4 font-epilogue">
              Limitation of Liability
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              To the fullest extent permitted by law:
            </p>
            <ul className="list-disc pl-5  text-neutral-800 mb-4 font-satoshi text-xs">
              <li>The Service is provided "as is," without warranties</li>
              <li>We are not liable for any errors in AI-generated content</li>
              <li>
                We are not liable for indirect, incidental, or special damages
              </li>
              <li>
                Our total liability is limited to what you paid us in the 12
                months prior to the claim
              </li>
            </ul>
            <p className=" text-neutral-800 font-satoshi">
              Some regions don't allow certain limitations, so this may not
              apply to you.
            </p>
          </section>

          <section id="termination" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900mb-4 font-epilogue">
              Termination
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              You may stop using Inkriv at any time. We may suspend or terminate
              your access if:
            </p>
            <ul className="list-disc pl-5  text-neutral-800 mb-4 font-satoshi text-xs">
              <li>You violate these Terms</li>
              <li>We detect fraud or abuse</li>
              <li>You fail to pay fees due</li>
              <li>We're required to do so by law</li>
            </ul>
            <p className=" text-neutral-800 font-satoshi">
              If your account is terminated, your access to content and account
              data may be removed. We recommend backing up any important
              content.
            </p>
          </section>

          <section id="changes" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900mb-4 font-epilogue">
              Changes to Terms
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              We may update these Terms occasionally. If changes are
              significant, we'll notify you via email or through the Service.
            </p>
            <p className=" text-neutral-800 font-satoshi">
              By continuing to use Inkriv after changes go into effect, you
              agree to the updated Terms.
            </p>
          </section>

          <section id="governing" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900mb-4 font-epilogue">
              Governing Law
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              These Terms are governed by the laws of Luxembourg, unless
              consumer protection laws in your country require otherwise. Inkriv
              is currently operated from Luxembourg. Disputes will be handled by
              the appropriate courts in Luxembourg, unless required otherwise by
              law.
            </p>
          </section>

          <section id="contact" className="mb-10">
            <h2 className="text-md font-semibold text-neutral-900mb-4 font-epilogue">
              Contact Us
            </h2>
            <p className=" text-neutral-800 mb-4 font-satoshi">
              If you have questions or concerns about these Terms, reach out:
            </p>
            <div className="bg-primary-50 p-6 rounded-md text-center mt-3">
              <p className=" font-semibold text-primary-700 mb-2 font-epilogue">
                Email: support@inkriv.com
              </p>
              <p className=" text-primary-600 font-satoshi">
                We're here to help and will respond to your inquiries as soon as
                possible.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
